import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTour } from './TourContext';
import { tourSteps } from './tourSteps';
import { cn } from '@/utils/helpers';

interface SpotlightPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function Tour() {
  const { state, nextStep, prevStep, skipTour, goToStep } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [spotlightPosition, setSpotlightPosition] = useState<SpotlightPosition | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const currentStep = tourSteps[state.currentStep];
  const isFirstStep = state.currentStep === 0;
  const isLastStep = state.currentStep === tourSteps.length - 1;
  const isCenterStep = currentStep?.placement === 'center';

  // Detect mobile and tablet
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640); // sm breakpoint
      setIsTablet(width >= 640 && width < 1024); // sm to lg
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Update spotlight position - returns true if spotlight was found
  const updateSpotlight = useCallback((): boolean => {
    if (!currentStep?.target || isCenterStep) {
      setSpotlightPosition(null);
      return false;
    }

    const element = document.querySelector(currentStep.target);
    if (element) {
      const rect = element.getBoundingClientRect();
      const padding = currentStep.spotlightPadding ?? 8;
      setSpotlightPosition({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });
      return true;
    } else {
      setSpotlightPosition(null);
      return false;
    }
  }, [currentStep, isCenterStep]);

  // Reset ready state when step changes
  useEffect(() => {
    setIsReady(false);

    // If step has an action, wait longer for action + spotlight update
    // Action runs at 300ms, spotlight re-update at 300+500=800ms
    const delay = currentStep?.action ? 900 : 100;
    const timer = setTimeout(() => {
      updateSpotlight();
      // Small delay for React to process state
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsReady(true));
      });
    }, delay);
    return () => clearTimeout(timer);
  }, [state.currentStep, currentStep?.action, updateSpotlight]);

  // Navigate to appropriate route for the step and execute action
  useEffect(() => {
    if (!state.isActive) return;

    // Navigate to route if needed
    if (currentStep?.route && location.pathname !== currentStep.route) {
      navigate(currentStep.route);
    }

    // Execute action after a small delay to ensure DOM is ready
    if (currentStep?.action) {
      const timer = setTimeout(() => {
        currentStep.action?.();
        // Re-update spotlight after action executes (for elements that appear after action)
        setTimeout(updateSpotlight, 500);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [state.isActive, state.currentStep, currentStep?.route, currentStep?.action, location.pathname, navigate, updateSpotlight]);

  useEffect(() => {
    if (!state.isActive) return;

    updateSpotlight();
    window.addEventListener('resize', updateSpotlight);
    window.addEventListener('scroll', updateSpotlight);

    return () => {
      window.removeEventListener('resize', updateSpotlight);
      window.removeEventListener('scroll', updateSpotlight);
    };
  }, [state.isActive, state.currentStep, updateSpotlight]);

  // Auto-skip desktop-only steps on mobile/tablet
  useEffect(() => {
    if (!state.isActive || !currentStep) return;
    if (currentStep.desktopOnly && (isMobile || isTablet)) {
      nextStep();
    }
  }, [state.isActive, state.currentStep, currentStep, isMobile, isTablet, nextStep]);

  // Keyboard navigation
  useEffect(() => {
    if (!state.isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skipTour();
      if (e.key === 'ArrowRight' || e.key === 'Enter') nextStep();
      if (e.key === 'ArrowLeft' && !isFirstStep) prevStep();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.isActive, skipTour, nextStep, prevStep, isFirstStep]);

  // Disable tour entirely on mobile/tablet — the UI layout is different
  if (isMobile || isTablet) {
    if (state.isActive) skipTour();
    return null;
  }

  if (!state.isActive || !currentStep) return null;

  // Should show as bottom sheet (no spotlight found on desktop)
  const hasSpotlight = spotlightPosition !== null;
  const showAsBottomSheet = isMobile || isTablet || (!isCenterStep && !hasSpotlight);

  const getTooltipPosition = (): React.CSSProperties => {
    // On mobile/tablet or no spotlight, don't use absolute positioning
    if (showAsBottomSheet || !spotlightPosition) {
      return {};
    }

    const { placement } = currentStep;
    const padding = 16;
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = spotlightPosition.top;
    let left = spotlightPosition.left;

    switch (placement) {
      case 'right':
        left = spotlightPosition.left + spotlightPosition.width + padding;
        // Check if tooltip would overflow right
        if (left + tooltipWidth > viewportWidth - padding) {
          left = spotlightPosition.left - tooltipWidth - padding;
        }
        break;
      case 'left':
        left = spotlightPosition.left - tooltipWidth - padding;
        // Check if tooltip would overflow left
        if (left < padding) {
          left = spotlightPosition.left + spotlightPosition.width + padding;
        }
        break;
      case 'bottom':
        top = spotlightPosition.top + spotlightPosition.height + padding;
        // Check if tooltip would overflow bottom
        if (top + tooltipHeight > viewportHeight - padding) {
          top = spotlightPosition.top - tooltipHeight - padding;
        }
        break;
      case 'top':
        top = spotlightPosition.top - tooltipHeight - padding;
        // Check if tooltip would overflow top
        if (top < padding) {
          top = spotlightPosition.top + spotlightPosition.height + padding;
        }
        break;
    }

    // Ensure tooltip stays within viewport
    top = Math.max(padding, Math.min(top, viewportHeight - tooltipHeight - padding));
    left = Math.max(padding, Math.min(left, viewportWidth - tooltipWidth - padding));

    return { top, left };
  };

  const content = (
    <div
      className="fixed inset-0 z-[9999] bg-black/60"
      aria-modal="true"
      role="dialog"
    >
      {/* Spotlight cutout - show on all devices when there's a target */}
      <AnimatePresence>
        {isReady && spotlightPosition && (
          <motion.div
            key="spotlight"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{
              duration: 0.25,
              ease: [0.4, 0, 0.2, 1]
            }}
            className="absolute bg-transparent rounded-xl"
            style={{
              top: spotlightPosition.top,
              left: spotlightPosition.left,
              width: spotlightPosition.width,
              height: spotlightPosition.height,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
            }}
          />
        )}
      </AnimatePresence>

      {/* Tooltip - Desktop positioned, Mobile/Tablet bottom sheet, Center modal */}
      <AnimatePresence mode="wait">
        {isReady && (
          <motion.div
            key={currentStep.id}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{
              duration: 0.2,
              ease: [0.4, 0, 0.2, 1]
            }}
            className={cn(
              'bg-white dark:bg-gray-900 rounded-2xl shadow-2xl',
              'border border-gray-200 dark:border-gray-700',
              isMobile ? 'max-h-[60vh] overflow-y-auto' : 'max-h-[80vh] overflow-y-auto',
              isCenterStep
                ? 'fixed inset-0 m-auto w-[calc(100%-32px)] sm:w-full max-w-md h-fit'
                : showAsBottomSheet
                  ? cn(
                      'fixed left-1/2 -translate-x-1/2 w-[calc(100%-32px)] max-w-md',
                      (isMobile || isTablet) ? 'bottom-20' : 'bottom-4'
                    )
                  : 'absolute w-80'
            )}
            style={!showAsBottomSheet && !isCenterStep ? getTooltipPosition() : undefined}
          >
          {/* Close button */}
          <button
            onClick={skipTour}
            className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close tour"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="p-5 sm:p-6">
            {/* Icon */}
            {currentStep.icon && (
              <div className="mb-4 flex justify-center sm:justify-start">
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800">
                  {currentStep.icon}
                </div>
              </div>
            )}

            {/* Title */}
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2 text-center sm:text-left">
              {currentStep.title}
            </h3>

            {/* Description */}
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed text-center sm:text-left">
              {currentStep.description}
            </p>
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-1 mb-4">
              {/* Show dots on larger screens, compact on mobile */}
              {isMobile ? (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {state.currentStep + 1} of {tourSteps.length}
                </span>
              ) : (
                tourSteps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToStep(index)}
                    className={cn(
                      'h-1.5 rounded-full transition-all duration-200',
                      index === state.currentStep
                        ? 'w-5 bg-indigo-500'
                        : index < state.currentStep
                        ? 'w-1.5 bg-indigo-300 dark:bg-indigo-700'
                        : 'w-1.5 bg-gray-200 dark:bg-gray-700'
                    )}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))
              )}
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-2">
              {/* Skip button */}
              <button
                onClick={skipTour}
                className="px-3 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors touch-manipulation"
              >
                Skip
              </button>

              {/* Nav buttons */}
              <div className="flex items-center gap-2">
                {!isFirstStep && (
                  <button
                    onClick={prevStep}
                    className="flex items-center justify-center gap-1 px-3 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors touch-manipulation min-w-[44px]"
                    aria-label="Previous step"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                    <span className="hidden sm:inline">Back</span>
                  </button>
                )}
                <button
                  onClick={nextStep}
                  className="flex items-center justify-center gap-1 px-4 py-2.5 text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 active:bg-indigo-700 rounded-lg transition-colors touch-manipulation min-w-[80px]"
                >
                  <span>{isLastStep ? 'Done' : 'Next'}</span>
                  {!isLastStep && <ChevronRightIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return createPortal(content, document.body);
}
